import { RestaurantAccesses } from '~/lib/accesses/RestaurantAccesses.frag'
import type { FragmentType } from '~/lib/graphql'
import { getFragmentData } from '~/lib/graphql'
import { css, cx, sva } from '~/styled-system/css'
import { hstack, vstack } from '~/styled-system/patterns'
import { RestaurantFaqFragment } from '../shared/RestaurantFaq.frag'

type Props = {
  restaurant: FragmentType<typeof RestaurantFaqFragment> & FragmentType<typeof RestaurantAccesses>
}

const restaurantFaqStyles = sva({
  slots: ['title', 'faqs', 'openingHours'],
  base: {
    title: {
      fontSize: '3xl',
      fontWeight: 'bold',
    },
    faqs: vstack.raw({
      gap: '5',
      pt: '5',
    }),
    openingHours: vstack.raw({
      gap: '3',
      alignItems: 'start',
    }),
  },
})

export function RestaurantFaq({ restaurant }: Props) {
  const faq = getFragmentData(RestaurantFaqFragment, restaurant)
  const access = getFragmentData(RestaurantAccesses, restaurant).accesses.find(
    (access) => access.__typename === 'RestaurantAccessTrain'
  )

  const classes = restaurantFaqStyles()
  return (
    <section>
      <h3 className={classes.title}>よくあるご質問</h3>
      <ul itemScope itemType="https://schema.org/FAQPage" className={classes.faqs}>
        {access && (
          <FaqItem question="この店舗の最寄りの駅からの行き方は？">
            {access.line}線　{access.station}駅　{access.note}
          </FaqItem>
        )}
        <FaqItem question="この店舗の営業時間は？">
          <span className={classes.openingHours}>
            <span>{faq.openingHoursText}</span>
            <span>※（）内の時間はラストオーダーの時間です。</span>
          </span>
        </FaqItem>
      </ul>
    </section>
  )
}

const faqItemStyles = sva({
  slots: ['root', 'initialLetter', 'question', 'questionText', 'answer', 'answerText'],
  base: {
    root: {
      border: 'medium',
      divideY: 'thin',
      divideColor: 'gray.200',
      divideStyle: 'dashed',
      dsY: '4',
      p: '6',
      w: 'full',
    },
    initialLetter: {
      fontSize: 'xl',
      fontWeight: 'bold',
    },
    question: hstack.raw({
      gap: '1',
    }),
    questionText: {
      fontSize: 'xl',
      fontWeight: 'bold',
    },
    answer: hstack.raw({
      gap: '1',
      alignItems: 'start',
      whiteSpace: 'pre-wrap',
    }),
    answerText: {
      fontSize: 'md',
    },
  },
})

function FaqItem({ question, children }: React.PropsWithChildren<{ question: string }>) {
  const classes = faqItemStyles()
  return (
    <li
      itemScope
      itemProp="mainEntity"
      itemType="https://schema.org/Question"
      className={classes.root}
    >
      <h4 className={classes.question}>
        <span className={classes.initialLetter}>Q.</span>
        <span itemProp="name" className={classes.questionText}>
          {question}
        </span>
      </h4>
      <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
        <p className={classes.answer}>
          <span className={cx(classes.initialLetter, css({ color: 'tertiary' }))}>A.</span>
          <span itemProp="text" className={classes.answerText}>
            {children}
          </span>
        </p>
      </div>
    </li>
  )
}
